import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@app/api/auth/useAuth';
import { withAuthLang } from '@app/i18n';
import AuthLayout from '@shared/ui/auth/AuthLayout';
import LoginHeader from '@shared/ui/auth/LoginHeader';
import { ThemeButton } from '@shared/ui/buttons';
import { EmailInput, MemberNameInput } from '@shared/ui/forms/inputs';
import { formatTimeRemaining, hasTimeLeft } from '@utils/date-helpers';
import { useCountdownTimer } from '@utils/hooks/useCountdownTimer';
import { getReferrer, saveReferrer } from '@utils/referrer';
import { validateServerErrors } from '@utils/validation';

const COOLDOWN_DURATION = 120; // seconds

export default function SignUpPage() {
  const [isSuccess, setIsSuccess] = useState(false);
  const [expiredAt, setExpiredAt] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { signup } = useAuth();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref');

  useEffect(() => {
    if (ref) {
      saveReferrer(ref);
    }
  }, [ref]);

  const timeRemaining = useCountdownTimer(expiredAt);
  const isCooldown = hasTimeLeft(timeRemaining);

  const {
    control,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: '',
      name: '',
    },
  });
  const { name, email } = getValues();

  async function onSubmit(data) {
    if (isCooldown) {
      return;
    }
    try {
      const referrer = getReferrer();
      const res = await signup(referrer ? { ...data, referrer } : data);
      if (res) {
        setIsSuccess(true);
        setExpiredAt(
          new Date(Date.now() + COOLDOWN_DURATION * 1000).toISOString(),
        );
      }
    } catch (error) {
      validateServerErrors(setError, error);
    }
  }

  return (
    <AuthLayout>
      {!isSuccess ? (
        <form className="auth__block" onSubmit={handleSubmit(onSubmit)}>
          <div className="block-pd-x block-pd-y">
            <LoginHeader />
            <div className="form">
              <EmailInput errors={errors} control={control} />
              <MemberNameInput errors={errors} control={control} />
            </div>
          </div>
          <div className="dialog-actions">
            <ThemeButton
              type="submit"
              endIcon="arrow-right"
              loading={isSubmitting}
            >
              {t('sign_up_btn')}
            </ThemeButton>
          </div>
        </form>
      ) : (
        <div className="auth__block">
          <div className="block-pd-x block-pd-y">
            <img
              className="auth__icon email-sent"
              src="/images/email-sent.png"
              alt="email sent"
              width={300}
              height={136}
            />
            <h2 className="title-h2m">{t('email_sent')}</h2>
            <p className="auth__subtitle">
              <span className="fw-500">{name}</span>, {t('email_sent_1')}{' '}
              <span className="fw-500">{email}</span>.
            </p>
            <p className="auth__subtitle">{t('email_sent_2')}</p>
            <p
              className="auth__subtitle"
              dangerouslySetInnerHTML={{ __html: t('email_sent_3') }}
            ></p>
          </div>
          <div className="dialog-actions btns-toolbar">
            <ThemeButton
              endIcon={isCooldown ? undefined : 'send'}
              disabled={isCooldown}
              loading={isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              {isCooldown ? (
                <>
                  <span className="cooldown">
                    {formatTimeRemaining(timeRemaining)}
                  </span>
                  {t('resend')}
                </>
              ) : (
                t('resend')
              )}
            </ThemeButton>
            <ThemeButton
              appearance="secondary"
              startIcon="chevron-left"
              onClick={() => navigate(withAuthLang('/login'))}
            >
              {t('back_to_login')}
            </ThemeButton>
          </div>
        </div>
      )}
    </AuthLayout>
  );
}
